<script setup>
import { usePage } from '@inertiajs/vue3'
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PredefinedValuesInputComponent from '@/components/PredefinedValuesInputComponent.vue'
import { useTransactionStore } from '@/stores/transaction'

// RED-267: user-entered land-parcel enrichment on an unbuilt (KASI-like,
// hoonestatud=0) transaction. Persists per-field into transaction.maatukk via
// the dedicated endpoint. Suggestion pools are SHARED with the Detail-view
// counterpart fields where one exists (infra.juurdepaas, the Kiirpäring
// maakatastrist kinnistu_haljastus, the plannings-card detailplaneering,
// ehrCommunal.side) so admin-curated values and saved user phrases appear in
// both places. Tehnovõrgud is a SECTION of separate per-network fields named
// after the Tehnovarustus (ehrCommunal) fields — structured maatukk.* keys,
// so a future land dataset gets features instead of one free-text composite;
// C2 composes them into the single Tehnovõrgud kohandustabel row.
// Display format mirrors the Ehitis tab: striped label/value DataTable.
const props = defineProps({
  transaction: { type: Object, required: true },
})

const { t } = useI18n()

const page = usePage()
const canEditTransaction = computed(
  () => page.props.auth?.can?.editTransaction ?? false,
)

const transactionStore = useTransactionStore()

// field = the transaction.maatukk key; category/name = the suggestion pool;
// labelKey defaults to messages.transactions.maatukk.<field>.
const GROUPS = [
  {
    fields: [
      { field: 'juurdepaas', category: 'infra', name: 'juurdepaas' },
      { field: 'ehitusoigus', category: 'landboard', name: 'detailplaneering' },
    ],
  },
  {
    headingKey: 'messages.transactions.maatukk.tehnovorgud',
    fields: [
      { field: 'elektrisysteemi_liik', category: 'maatukk', name: 'elektrisysteemi_liik' },
      { field: 'veevarustuse_liik', category: 'maatukk', name: 'veevarustuse_liik' },
      { field: 'kanalisatsiooni_liik', category: 'maatukk', name: 'kanalisatsiooni_liik' },
      { field: 'soojusvarustuse_liik', category: 'maatukk', name: 'soojusvarustuse_liik' },
      { field: 'gaasi_olemasolu', category: 'maatukk', name: 'gaasi_olemasolu' },
      { field: 'side', category: 'ehrCommunal', name: 'side' },
    ].map(f => ({ ...f, labelKey: `messages.detail.ehrCommunal.${f.field}` })),
  },
  {
    fields: [
      { field: 'haljastus', category: 'landboard', name: 'kinnistu_haljastus' },
      { field: 'muu', category: 'maatukk', name: 'muu' },
    ],
  },
]

const FIELDS = GROUPS.flatMap(group => group.fields)

const rows = computed(() =>
  GROUPS.flatMap(group => [
    ...(group.headingKey
      ? [{ key: `heading-${group.headingKey}`, heading: t(group.headingKey) }]
      : []),
    ...group.fields.map(f => ({
      key: f.field,
      label: t(f.labelKey ?? `messages.transactions.maatukk.${f.field}`),
      ...f,
    })),
  ]),
)

const values = reactive({})

watch(
  () => props.transaction,
  transaction => {
    FIELDS.forEach(({ field }) => {
      values[field] = transaction?.maatukk?.[field] ?? ''
    })
  },
  { immediate: true },
)

// Saved on blur + suggestion select (not per keystroke — the maatukk endpoint
// has no debounce layer). The dirty-check stops the blur-after-select double.
const save = field => {
  const newValue = values[field] ?? ''
  const oldValue = props.transaction?.maatukk?.[field] ?? ''
  if (newValue === oldValue) {
    return
  }

  transactionStore.updateTransactionMaatukk({
    transactionId: props.transaction.id,
    field,
    value: newValue,
  })
}
</script>

<template>
  <div data-testid="txn-sidebar-maatukk" class="maatukk-info-table">
    <DataTable
      stripedRows
      hover
      :value="rows"
      :dt="{
        bodyCell: {
          padding: '0.5rem 1rem',
        },
      }"
    >
      <Column field="label" :pt="{ bodyCell: { class: 'w-1/3 align-top' } }" :ptOptions="{ mergeProps: true }">
        <template #body="slotProps">
          <span v-if="slotProps.data.heading" class="block font-bold">{{ slotProps.data.heading }}</span>
          <span v-else class="block">{{ slotProps.data.label }}</span>
        </template>
      </Column>
      <Column field="value">
        <template #body="slotProps">
          <template v-if="!slotProps.data.heading">
            <PredefinedValuesInputComponent
              v-if="canEditTransaction"
              :category="slotProps.data.category"
              :name="slotProps.data.name"
              :append-on-select="true"
              v-model="values[slotProps.data.field]"
              :data-testid="`txn-sidebar-maatukk-${slotProps.data.field}`"
              @blur="save(slotProps.data.field)"
              @selected="save(slotProps.data.field)"
            />
            <span
              v-else
              class="whitespace-pre-wrap"
              :data-testid="`txn-sidebar-maatukk-${slotProps.data.field}`"
            >
              {{ values[slotProps.data.field] || '' }}
            </span>
          </template>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped>
/* The suggestion dropdown is absolutely positioned inside the value cell —
   the DataTable's scroll container would clip it. This table never needs to
   scroll itself (the Drawer panel scrolls), so let overflow escape.
   !important: PrimeVue writes overflow:auto as an INLINE style on the
   container, which only an !important stylesheet rule can override. */
.maatukk-info-table :deep(.p-datatable-table-container) {
  overflow: visible !important;
}
</style>
